import {
  PHYSICAL_VENUE_MODELS,
  LINEAR_FORMATS,
  VERIFIED_BY_LABELS,
  SESSION_STRUCTURE_LABELS,
  VENUE_MODEL_LABELS,
  ACTIVITY_LABELS,
  THEME_LABELS,
  REPLAYABILITY_LABELS,
  GUIDANCE_LEVEL_LABELS,
  labelFromMap,
  labelsFromSlugs,
  isPhysicalPrimaryFormat
} from './experienceOntology';
import { normalizeExperience } from './normalizeExperience';

/** Infer primary store/service from launch URL when not explicitly set. */
function inferPublishPlatform(exp) {
  if (exp.publishPlatform) return exp.publishPlatform;
  const url = String(exp.url || exp.launchUrl || '').toLowerCase();

  if (/youtube\.com|youtu\.be/.test(url)) return 'YouTube';
  if (/apps\.apple\.com/.test(url)) return 'Apple App Store';
  if (/play\.google\.com/.test(url)) return 'Google Play';
  if (/meta\.com\/experiences/.test(url)) return 'Meta Quest Store';
  if (/store\.steampowered\.com/.test(url)) return 'Steam';
  if (exp.hostPlatform === 'vrchat') return 'Inside VRChat';
  if (exp.isLBE || exp.isPhysicalVenue) return null;
  if (exp.launchInBrowser || /esc\.art|meetwol\.com|webar|blippar|sylvanerd/.test(url)) {
    return 'Web browser';
  }
  return null;
}

/** User-facing price string — always prefer explicit priceLabel when set. */
export function renderPriceDisplay(exp) {
  if (!exp) return null;
  if (exp.priceLabel) return exp.priceLabel;
  if (exp.priceType === 'free') return 'Free';
  if (exp.priceType === 'ticket') return 'Tickets required';
  if (exp.priceType === 'paid') return 'Paid';
  return null;
}

/**
 * Where to get the experience — primary publisher/store plus optional also-on lines.
 * e.g. "YouTube" + "Also on Meta Quest (YouTube app)"
 */
export function renderPublishPlatformLine(exp) {
  if (!exp) return null;

  const primary = inferPublishPlatform(exp);
  const also = Array.isArray(exp.alsoAvailableOn) ? exp.alsoAvailableOn.filter(Boolean) : [];

  if (!primary && also.length === 0) return null;
  if (primary && also.length === 0) return primary;
  if (!primary && also.length > 0) return `Also on ${also.join(' · ')}`;
  return `${primary} · Also on ${also.join(' · ')}`;
}

const QUICK_FACT_BUILDERS = {
  location(exp) {
    const parts = [exp.venueName, exp.venueCity || exp.locationCity].filter(Boolean);
    if (parts.length > 0) return parts.join(' · ');
    if (exp.duration && (exp.isLBE || exp.isPhysicalVenue)) {
      return exp.duration.split(',')[0].trim();
    }
    return null;
  },
  visitTime(exp) {
    return exp.recommendedVisitTime || exp.venueHours || null;
  },
  price(exp) {
    return renderPriceDisplay(exp);
  },
  publishPlatform(exp) {
    return renderPublishPlatformLine(exp);
  },
  age(exp) {
    return exp.ageDisplay || null;
  },
  platform(exp) {
    if (!exp.platformLabels?.length) return null;
    return exp.platformLabels.join(' · ');
  },
  multiplayer(exp) {
    if (exp.multiplayer === true) return 'Multiplayer';
    if (exp.multiplayer === false) return 'Single Player';
    return null;
  },
  sessionLength(exp) {
    if (exp.recommendedVisitTime) return null;
    if (exp.typicalSessionLabel) return exp.typicalSessionLabel;
    if (exp.runtimeMinutes) return `${exp.runtimeMinutes} min`;
    return null;
  },
  devices(exp) {
    if (!exp.deviceLabels?.length) return null;
    return exp.deviceLabels.join(' · ');
  },
  runtime(exp) {
    if (exp.recommendedVisitTime) return null;
    if (exp.runtimeMinutes) return `${exp.runtimeMinutes} min runtime`;
    if (exp.typicalSessionLabel && !exp.isPhysicalVenue) return exp.typicalSessionLabel;
    return null;
  },
  whereToWatch(exp) {
    const publish = renderPublishPlatformLine(exp);
    if (publish) return publish;
    if (exp.platformLabels?.length) return `Watch on ${exp.platformLabels.join(', ')}`;
    if (exp.launchUrl) return 'Watch in browser';
    return null;
  },
  language(exp) {
    if (!exp.languages?.length) return null;
    return exp.languages.join(' · ');
  },
  nextDate(exp) {
    return exp.availabilityStatusLabel || null;
  },
  venue(exp) {
    const parts = [exp.venueName, exp.venueCity].filter(Boolean);
    if (parts.length === 0) return null;
    return parts.join(' · ');
  },
  freeJoin(exp) {
    if (exp.priceType === 'free') return 'Free to join';
    if (exp.priceLabel?.toLowerCase().includes('free')) return 'Free to join';
    return null;
  },
  socialSize(exp) {
    if (exp.groupSize?.length) return exp.groupSize.join(' · ');
    if (exp.multiplayer === true) return 'Social experience';
    return null;
  }
};

const QUICK_FACT_PROFILES = {
  physical: ['location', 'visitTime', 'age', 'venue', 'sessionLength'],
  vr: ['platform', 'multiplayer', 'sessionLength'],
  ar: ['devices', 'sessionLength', 'age'],
  linear: ['runtime', 'language'],
  scheduled: ['nextDate', 'venue', 'age'],
  persistent: ['platform', 'socialSize', 'sessionLength']
};

function resolveQuickFactProfile(exp) {
  if (exp.isPhysicalVenue || PHYSICAL_VENUE_MODELS.includes(exp.venueModel)) return 'physical';
  if (exp.availabilityType === 'scheduled' || exp.availabilityType === 'limited-run') return 'scheduled';
  if (exp.availabilityType === 'persistent' || exp.venueModel === 'virtual-world') return 'persistent';
  if (exp.primaryFormat && LINEAR_FORMATS.includes(exp.primaryFormat)) return 'linear';
  if (exp.primaryFormat === 'vr' || exp.primaryFormat === 'social-vr') return 'vr';
  if (exp.primaryFormat === 'ar' || exp.primaryFormat === 'webxr') return 'ar';
  if (exp.primaryFormat === 'audio-walk') return 'ar';
  return 'vr';
}

/**
 * Returns 3–6 user-facing quick fact strings, or [] if fewer than 3 available.
 */
export function renderQuickFacts(rawExperience) {
  const exp = normalizeExperience(rawExperience);
  if (!exp) return [];

  const profile = resolveQuickFactProfile(exp);
  const priorityKeys = QUICK_FACT_PROFILES[profile] || QUICK_FACT_PROFILES.vr;

  const facts = [];
  const seen = new Set();

  for (const key of priorityKeys) {
    if (facts.length >= 6) break;
    const builder = QUICK_FACT_BUILDERS[key];
    if (!builder) continue;
    const fact = builder(exp);
    if (!fact || seen.has(fact)) continue;
    seen.add(fact);
    facts.push(fact);
  }

  if (facts.length < 3) return [];
  return facts.slice(0, 6);
}

const TICKET_PRICE_TYPES = new Set(['ticket', 'paid']);

const NATIVE_APP_SURFACES = new Set(['mobile-app', 'headset-app', 'desktop-app']);
const WEB_DELIVERY_TECH = new Set(['webxr', 'webar', 'webvr']);
const WEB_ACCESS_SURFACES = new Set(['mobile-browser', 'desktop-browser', 'headset-browser']);

function listIncludesAny(list, candidates) {
  if (!Array.isArray(list) || list.length === 0) return false;
  const normalized = new Set(list.map((v) => String(v).toLowerCase()));
  return [...candidates].some((c) => normalized.has(String(c).toLowerCase()));
}

function requiresNativeAppDownload(exp) {
  if (isJoinWorldExperience(exp)) return false;
  if (exp.isPlatformApp) return true;
  return listIncludesAny(exp.deliveryTechnology, ['native-app']);
}

function isWebBasedExperience(exp) {
  return (
    listIncludesAny(exp.deliveryTechnology, WEB_DELIVERY_TECH) ||
    listIncludesAny(exp.accessSurface, WEB_ACCESS_SURFACES)
  );
}

/** Worlds and events inside a downloaded platform (e.g. VRChat worlds) — not the platform app itself. */
function isJoinWorldExperience(exp) {
  if (exp.isPlatformApp) return false;
  if (exp.hostPlatform) return true;
  if (exp.primaryFormat === 'social-vr' && exp.sessionStructure === 'live-event') return true;
  return false;
}

function isPhysicalVenueExperience(exp) {
  return exp.isPhysicalVenue || PHYSICAL_VENUE_MODELS.includes(exp.venueModel) || exp.isLBE;
}

/** Free or walk-in physical installation — no ticket or reservation flow. */
function isOpenAccessPhysical(exp) {
  if (!isPhysicalVenueExperience(exp)) return false;
  if (isJoinWorldExperience(exp)) return false;
  if (exp.priceType === 'ticket' || exp.priceType === 'paid') return false;
  if (exp.availabilityType === 'scheduled' || exp.availabilityType === 'limited-run') return false;
  if (exp.venueModel === 'touring') return false;
  return true;
}

function requiresTicketOrReservation(exp) {
  if (isJoinWorldExperience(exp)) return false;
  // Tickets and reservations are for physical venues — not app store purchases or digital streams.
  if (!isPhysicalVenueExperience(exp)) return false;
  return (
    exp.priceType === 'ticket' ||
    exp.availabilityType === 'scheduled' ||
    exp.availabilityType === 'limited-run' ||
    exp.venueModel === 'touring'
  );
}

export function isGetLocationCta(exp) {
  return isOpenAccessPhysical(exp);
}

export function isGetTicketsCta(exp) {
  return requiresTicketOrReservation(exp);
}

/** @deprecated Use isGetLocationCta */
export function isPlanYourVisitCta(exp) {
  return isGetLocationCta(exp);
}

function buildMapsUrl(exp) {
  if (exp.venueLat != null && exp.venueLng != null) {
    return `https://www.google.com/maps/search/?api=1&query=${exp.venueLat},${exp.venueLng}`;
  }
  const address = [exp.venueName, exp.venueAddress, exp.venueCity, exp.venueCountry]
    .filter(Boolean)
    .join(', ');
  if (address) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  }
  return null;
}

/** Resolves where the primary CTA should navigate. */
export function resolvePrimaryCtaAction(rawExperience) {
  const exp = normalizeExperience(rawExperience);
  if (!exp) return null;

  if (isGetLocationCta(exp)) {
    const mapsUrl = buildMapsUrl(exp);
    if (mapsUrl) return { type: 'maps', url: mapsUrl };
    if (exp.launchUrl) return { type: 'url', url: exp.launchUrl };
    return null;
  }

  if (exp.launchUrl) return { type: 'url', url: exp.launchUrl };
  return null;
}

export function renderCtaLabel(rawExperience) {
  const exp = normalizeExperience(rawExperience);
  if (!exp) return 'Launch Experience';

  if (isJoinWorldExperience(exp)) return 'Join World';
  if (isOpenAccessPhysical(exp)) return 'Get Location';
  if (requiresTicketOrReservation(exp)) return 'Get Tickets';
  if (requiresNativeAppDownload(exp)) return 'Download App';
  if (isWebBasedExperience(exp)) return 'Launch Experience';
  return 'Launch Experience';
}

export function renderCtaMicrocopy(rawExperience) {
  const exp = normalizeExperience(rawExperience);
  if (!exp) return null;

  // Price + publish platform shown prominently above the CTA — microcopy is action-only here.
  if (isGetLocationCta(exp)) {
    if (exp.venueHours) return 'Check hours before you go';
    return 'Opens in your maps app';
  }

  if (isGetTicketsCta(exp)) {
    const needsReservation =
      exp.priceType === 'free' ||
      /free/i.test(exp.priceLabel || '') ||
      exp.availabilityType === 'scheduled' ||
      exp.availabilityType === 'limited-run';
    if (needsReservation) return 'Reservation may be required';
    return 'Official ticketing';
  }

  if (isJoinWorldExperience(exp)) {
    return exp.hostPlatform === 'vrchat' ? 'Requires VRChat on Meta Quest or Steam' : 'Opens inside the app';
  }

  if (requiresNativeAppDownload(exp)) {
    return 'Opens in the store or download page';
  }

  if (isWebBasedExperience(exp) || exp.launchInBrowser) {
    return 'Opens in your browser';
  }

  return null;
}

export function renderExploreSectionOrder(rawExperience) {
  const exp = normalizeExperience(rawExperience);
  if (!exp) return ['gallery', 'about', 'accessibility', 'creator'];

  if (PHYSICAL_VENUE_MODELS.includes(exp.venueModel)) {
    return ['availability', 'gallery', 'about', 'accessibility', 'creator'];
  }
  return ['gallery', 'about', 'accessibility', 'creator'];
}

const AVAILABILITY_STATUS_CONFIG = {
  'available-now': { label: 'Available now', color: '#00c853' },
  'open-today': { label: 'Open today', color: '#00c853', appendHours: true },
  'next-show': { label: 'Next show', color: '#ffc107', useCustomLabel: true },
  'limited-availability': { label: 'Limited availability', color: '#ffc107', useCustomLabel: true },
  'coming-soon': { label: 'Coming soon', color: '#9e9e9e', useCustomLabel: true },
  'sold-out': { label: 'Sold out', color: '#f44336' },
  'season-ended': { label: 'Season ended', color: '#9e9e9e' }
};

export function renderAvailabilityStatus(rawExperience) {
  const exp = normalizeExperience(rawExperience);
  if (!exp?.availabilityStatus) return null;

  const config = AVAILABILITY_STATUS_CONFIG[exp.availabilityStatus];
  if (!config) return null;

  let label = config.useCustomLabel && exp.availabilityStatusLabel
    ? exp.availabilityStatusLabel
    : config.label;

  if (config.appendHours && exp.venueHours) {
    label = `${label} · ${exp.venueHours}`;
  } else if (exp.availabilityStatus === 'open-today' && !exp.venueHours && exp.availabilityStatusLabel) {
    label = exp.availabilityStatusLabel;
  }

  return { label, color: config.color };
}

export function renderVerifiedLine(rawExperience) {
  const exp = normalizeExperience(rawExperience);
  if (!exp?.verifiedBy) return null;
  const source = VERIFIED_BY_LABELS[exp.verifiedBy] || exp.verifiedBy;
  const updated = exp.lastUpdated ? ` · Updated ${exp.lastUpdated}` : '';
  return `Verified by ${source}${updated}`;
}

export function getReferenceFields(rawExperience) {
  const exp = normalizeExperience(rawExperience);
  if (!exp) return { experience: [], access: [], audience: [], time: [] };

  const experience = [];
  const access = [];
  const audience = [];
  const time = [];

  if (exp.primaryFormat) experience.push({ label: 'Format', value: exp.formatBadge || exp.primaryFormat });
  if (exp.themes?.length) {
    experience.push({ label: 'Themes', value: labelsFromSlugs(exp.themes, THEME_LABELS).join(', ') });
  }
  if (exp.activities?.length) {
    experience.push({ label: 'Activities', value: labelsFromSlugs(exp.activities, ACTIVITY_LABELS).join(', ') });
  }
  if (exp.sessionStructure) {
    experience.push({
      label: 'Session',
      value: labelFromMap(SESSION_STRUCTURE_LABELS, exp.sessionStructure)
    });
  }
  if (exp.replayability) {
    experience.push({
      label: 'Replayability',
      value: labelFromMap(REPLAYABILITY_LABELS, exp.replayability)
    });
  }
  if (exp.guidanceLevel) {
    experience.push({
      label: 'Guidance',
      value: labelFromMap(GUIDANCE_LEVEL_LABELS, exp.guidanceLevel)
    });
  }
  if (exp.effortSignal) experience.push({ label: 'Effort', value: exp.effortSignal });

  const price = renderPriceDisplay(exp);
  if (price) access.push({ label: 'Price', value: price });

  const publish = renderPublishPlatformLine(exp);
  if (publish) access.push({ label: 'Available on', value: publish });

  if (exp.platformLabels?.length) access.push({ label: 'Platforms', value: exp.platformLabels.join(', ') });
  if (exp.deviceLabels?.length) access.push({ label: 'Devices', value: exp.deviceLabels.join(', ') });
  if (exp.languages?.length) access.push({ label: 'Languages', value: exp.languages.join(', ') });
  if (exp.venueModel) {
    access.push({ label: 'Venue model', value: labelFromMap(VENUE_MODEL_LABELS, exp.venueModel) });
  }

  if (exp.ageDisplay) audience.push({ label: 'Age rating', value: exp.ageDisplay });
  if (exp.groupSize?.length) audience.push({ label: 'Group size', value: exp.groupSize.join(', ') });
  if (exp.contentNotes?.length) audience.push({ label: 'Content notes', value: exp.contentNotes.join(', ') });
  if (exp.accessibilityFeatures?.length) {
    audience.push({ label: 'Accessibility', value: exp.accessibilityFeatures.join(', ') });
  }

  if (exp.recommendedVisitTime) time.push({ label: 'Recommended visit', value: exp.recommendedVisitTime });
  else if (exp.typicalSessionLabel) time.push({ label: 'Typical session', value: exp.typicalSessionLabel });
  else if (exp.runtimeMinutes) time.push({ label: 'Runtime', value: `${exp.runtimeMinutes} min` });
  else if (exp.venueHours) time.push({ label: 'Hours', value: exp.venueHours });

  return { experience, access, audience, time };
}

export function hasAccessibilitySection(rawExperience) {
  const exp = normalizeExperience(rawExperience);
  if (!exp) return false;
  const isPhysical = isPhysicalPrimaryFormat(exp.primaryFormat);
  const showLocomotion = !isPhysical && exp.locomotionTypes?.length;
  return !!(
    showLocomotion ||
    exp.contentNotes?.length ||
    exp.accessibilityFeatures?.length ||
    exp.physicalRequirements?.length
  );
}

export function hasGallerySection(rawExperience) {
  const exp = normalizeExperience(rawExperience);
  if (!exp) return false;
  return exp.galleryUrls.length > 0;
}

export function hasCreatorSection(rawExperience) {
  const exp = normalizeExperience(rawExperience);
  if (!exp) return false;
  return !!(exp.creatorName || exp.creatorId);
}

export function getCompatibleSurfaces(rawExperience) {
  const exp = normalizeExperience(rawExperience);
  if (!exp) return [];
  const surfaces = [...(exp.platformLabels || []), ...(exp.deviceLabels || [])];
  return [...new Set(surfaces)];
}
